import React, { useState, useEffect, useCallback, Fragment } from 'react';
import { Breadcrumbs, H5 } from '../../../../AbstractElements';
import { Card, CardBody, CardHeader, Col, Container, Row, Input, Media } from 'reactstrap';
import DataTable from 'react-data-table-component';
import { PlusCircle, Edit, User, File, Trash2 } from 'react-feather';
import ExpenseDataModal from './ExpenseDataModal';
import { toast } from 'react-toastify';
import DeleteConfirmationModal from '../../../Common/Modals/DeleteConfirmationModal';
import { confirmDelete, fetchTableData } from '../../../../Api/ApiMethods';
import { useNavigate } from 'react-router-dom';

const Expenses = () => {
    const [selectedRows, setSelectedRows] = useState([]);
    const [clearSelectedRows, setClearSelectedRows] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState('');
    const [totalRows, setTotalRows] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [selectedExpense, setSelectedExpense] = useState(null);
    const navigate = useNavigate();

    const expenseApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/expense';

    const logSetData = (newData) => {
        console.log("Expenses.jsx: setData CALLED with new data length:", newData?.length, newData);
        setData(newData);
    };

    const handleRowSelected = useCallback(state => {
        setSelectedRows(state.selectedRows);
    }, []);

    const handleSearch = (event) => {
        event.preventDefault();
        setSearch(event.target.value);
        setPage(1);
    };

    useEffect(() => {
        fetchTableData({
            apiEndpoint: expenseApiUrl,
            page,
            pageSize,
            search,
            setDataCallback: logSetData,
            setTotalRowsCallback: setTotalRows,
        });
    }, [page, pageSize, search, expenseApiUrl]);

    const toggleDeleteModal = () => setDeleteModal(!deleteModal);

    const toggleModal = () => {
        setModalOpen(!modalOpen);
        if (modalOpen) {
            setTimeout(() => setSelectedExpense(null), 300);
        }
    };

    const openAddModal = () => {
        setModalMode('add');
        setSelectedExpense(null);
        setModalOpen(true);
    };

    const openModifyModal = (expense) => {
        setModalMode('modify');
        setSelectedExpense(expense);
        setModalOpen(true);
    };

    const handleCallForm = (row) => {
        const callFormId = row.callForm?.[0];

        if (callFormId) {
            navigate(`/callform/${callFormId}`);
        } else {
            console.warn("Call form ID not found or structure unexpected in row:", row);
        }
    };

    const handleCustomerDetail = (customerId) => {
        navigate(`/customer/${customerId}`);
    };

    const refreshExpenseData = () => {
        console.log("Expenses.jsx: refreshExpenseData CALLED. Current state:", { page, pageSize, search });
        fetchTableData({
            apiEndpoint: expenseApiUrl,
            page,
            pageSize,
            search,
            setDataCallback: logSetData,
            setTotalRowsCallback: setTotalRows,
        });
    };

    const renderCellWithTooltip = (row, field, defaultValue = '') => {
        const value = row[field] || defaultValue;
        return (
            <div
                title={value}
                style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '150px',
                }}
            >
                {value}
            </div>
        );
    };

    const renderCallFormLink = (row) => {
        const callFormId = row.callForm?.[0];
        return callFormId ? (
            <div className="btn btn-outline-info btn-sm d-flex align-items-center justify-content-center" onClick={() => handleCallForm(row)} title="Kaza Dosyasına Git">
                <File size={18} />
            </div>
        ) : '-';
    };

    const columns = [
        { name: 'Masraf Numarası', selector: row => row.expenseNumber || '', sortable: true, width: '160px' },
        {
            name: 'Masraf Türü',
            selector: row => row.expenseType || '',
            sortable: true,
            cell: row => renderCellWithTooltip(row, 'expenseType'),
            width: '150px'
        },
        { name: 'Tutar', selector: row => row.amount ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(row.amount) : '', sortable: true, width: '120px' },
        { name: 'Tarih ve Saat', selector: row => row.date ? new Date(row.date).toLocaleString() : '', sortable: true, width: '180px' },
        // { name: 'Durum', selector: row => row.paymentStatus || '', sortable: true, width: '140px' },
        {
            name: 'Fatura No',
            selector: row => row.invoiceNumber || '',
            sortable: true,
            cell: row => renderCellWithTooltip(row, 'invoiceNumber'),
            width: '150px'
        },
        {
            name: 'Açıklama',
            selector: row => row.description || '',
            sortable: true,
            cell: row => renderCellWithTooltip(row, 'description'),
            grow: 2
        },
        {
            name: 'Müşteri',
            cell: row => {
                const customerId = Array.isArray(row.customer)
                    ? row.customer[0]
                    : (typeof row.customer === 'object' && row.customer !== null ? row.customer.id : null);

                return customerId ? (
                    <div className="btn btn-outline-primary btn-sm d-flex align-items-center justify-content-center" onClick={() => handleCustomerDetail(customerId)} title="Müşteri Detayı">
                        <User size={18} />
                    </div>
                ) : '-';
            },
            ignoreRowClick: true,
            right: true,
            button: true,
        },
        {
            name: 'Çağrı Formu',
            cell: renderCallFormLink,
            ignoreRowClick: true,
            right: true,
            button: true,
        },
        {
            name: 'Düzenle',
            cell: (row) => (
                <div
                    className="btn btn-outline-warning d-flex align-items-center justify-content-center"
                    onClick={() => openModifyModal(row)}
                    title="Düzenle"
                >
                    <Edit size={18} />
                </div>
            ),
            ignoreRowClick: true,
            right: true,
            button: true,
        },
    ];

    return (
        <>
            <Breadcrumbs parent='Sayfalar' title='Masraflar' />
            <Container fluid={true}>
                <Row>
                    <Col sm='12'>
                        <Card>
                            <CardHeader>
                                <H5>Masraflar</H5>
                                <span>Mevcut olan masraflar aşağıda listelenmektedir.</span>
                            </CardHeader>
                            <CardBody>
                                <div className="d-flex justify-content-end align-items-center w-100 file-content flex-wrap">
                                    <div className='form-inline me-2 mt-3 p-0 custom-responsive-width'>
                                        <div className='mb-0 form-group d-flex align-items-center border rounded px-2' style={{ height: '35px' }}>
                                            <i className='fa fa-search text-muted'></i>
                                            <Input
                                                type="text"
                                                placeholder="Ara.."
                                                value={search}
                                                onChange={(e) => handleSearch(e)}
                                                className="border-0 ms-2 shadow-none"
                                                style={{ height: '33px', flexGrow: 1 }}
                                            />
                                        </div>
                                    </div>
                                    <Media body className='text-end mt-3'>
                                        <div className="btn btn-outline-primary ms-2" onClick={openAddModal} style={{ height: '35px', display: 'inline-flex', alignItems: 'center' }}>
                                            <PlusCircle size={18} className="me-1" />
                                            {' Masraf Ekle'}
                                        </div>
                                    </Media>
                                    {(selectedRows.length !== 0) &&
                                        <div className='btn btn-outline-danger ms-2 mt-3' onClick={toggleDeleteModal} style={{ height: '35px', display: 'inline-flex', alignItems: 'center' }}>
                                            <Trash2 size={18} className="me-1" />
                                            {' Seçilenleri Sil'}
                                        </div>
                                    }
                                </div>

                                <div style={{ height: "40px" }}></div>

                                <div className='table-responsive'>
                                    <DataTable
                                        columns={columns}
                                        data={data}
                                        onSelectedRowsChange={handleRowSelected}
                                        pagination
                                        paginationServer
                                        selectableRows
                                        clearSelectedRows={clearSelectedRows}
                                        paginationTotalRows={totalRows}
                                        onChangePage={page => setPage(page)}
                                        onChangeRowsPerPage={pageSize => { setPage(1); setPageSize(pageSize); }}
                                        striped
                                        noDataComponent={<div className="p-4 text-center">Masraf bulunamadı.</div>}
                                        progressPending={!data}
                                        progressComponent={<p>Yükleniyor...</p>}
                                    />
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
            <ExpenseDataModal
                apiEndpoint={expenseApiUrl}
                isOpen={modalOpen}
                toggle={toggleModal}
                mode={modalMode}
                selectedExpense={selectedExpense}
                refreshData={refreshExpenseData}
                customerId={null}
                autoAssignCustomer={false}
                accidentFile={[]}
            />
            <DeleteConfirmationModal
                isOpen={deleteModal}
                toggle={toggleDeleteModal}
                itemsToDelete={selectedRows}
                displayKey="expenseNumber"
                onConfirm={() =>
                    confirmDelete({
                        apiEndpoint: expenseApiUrl,
                        selectedItems: selectedRows,
                        toggleModalCallback: toggleDeleteModal,
                        clearSelectionCallback: () => {
                            setSelectedRows([]);
                            setClearSelectedRows(!clearSelectedRows);
                        },
                        refreshDataCallback: () =>
                            fetchTableData({
                                apiEndpoint: expenseApiUrl,
                                page,
                                pageSize,
                                search,
                                setDataCallback: logSetData,
                                setTotalRowsCallback: setTotalRows,
                            }),
                    })
                }
            />
        </>
    );
};

export default Expenses;
